import { bpeConfig } from '@enterprise/common';
import {
  createClient,
  createLDReactProviderWithClient,
  LDInspection,
  type LDReactClient,
  type LDReactClientContext
} from '@launchdarkly/react-sdk';
import { buildLaunchDarklyFlagUsedHandler } from '@sentry/react';
import React, { type FC, useEffect } from 'react';
import { useTenantConfig } from 'utils/hooks/useTenantConfig';

const isTest = process.env.NODE_ENV === 'test';

type PropsWithChildren = Required<React.PropsWithChildren>;

interface CreateLdProviderOptions {
  clientSideID: string | undefined;
  enabled: boolean;
  displayName: string;
  reactContext?: LDReactClientContext;
}

export interface LdProviderProps {
  userUid: string | undefined;
  personUid: string | undefined;
  webOrigin: string | undefined;
}

interface CreateLdClientAndProviderOutput {
  Provider: FC<PropsWithChildren>;
  client: LDReactClient | null;
}

export function createLdProvider({
  enabled,
  reactContext,
  clientSideID,
  displayName
}: CreateLdProviderOptions): CreateLdClientAndProviderOutput {
  if (!enabled || !clientSideID || isTest) {
    return {
      client: null,
      Provider: (props) => props.children
    };
  }
  const client = createClient(
    clientSideID,
    {
      kind: 'user',
      anonymous: true
    },
    {
      inspectors: [buildLaunchDarklyFlagUsedHandler() as LDInspection],
      wrapperName: displayName,
      diagnosticOptOut: true
    }
  );

  const LDProvider = createLDReactProviderWithClient(client, reactContext);
  client?.start();

  return {
    client,
    Provider: LDProvider
  };
}

export function useUserIdentification(
  LD: ReturnType<typeof createLdProvider>,
  { userUid, personUid, webOrigin }: LdProviderProps
) {
  const config = useTenantConfig();
  const targetWebOrigin = webOrigin || config?.tenantConfig?.webOrigin;

  useEffect(() => {
    async function identify() {
      if (!LD.client) {
        return;
      }
      await LD.client.identify({
        key: userUid ?? 'undefined_user',
        custom: {
          'tenant-code': bpeConfig.tenant.code,
          'tenant-namespace': bpeConfig.tenant.id,
          'person-uid': personUid ?? '',
          'web-origin': targetWebOrigin ?? ''
        }
      });
    }
    if (userUid && personUid && targetWebOrigin) {
      identify();
    }
  }, [LD.client, userUid, personUid, targetWebOrigin]);
}
